import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { cn } from "../../util";

import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import History from "@tiptap/extension-history";
import { EditorContent, useEditor } from "@tiptap/react";

import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ListBulletIcon,
  NumberedListIcon,
} from "@heroicons/react/24/outline";

const DEFAULT_LIST_HTML = `<div><ol><li><p></p></li></ol></div>`;

export function TipTapEditor({ value, onChange, error, label }) {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: value || DEFAULT_LIST_HTML,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML().trim();
      const isEmpty =
        html === "" ||
        html === "<p></p>" ||
        html === "<br>" ||
        html === "<p><br></p>";

      const hasList = html.includes("<ol") || html.includes("<ul");

      if (!hasList || isEmpty) {
        // Replace with default list
        editor.commands.setContent(DEFAULT_LIST_HTML, false); // false = retain history
        return;
      }
      onChange(html);
    },
  });

  useEffect(() => {
    if (editor && value && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div>
      <label className="block font-medium">{label}</label>

      <div className="relative w-full mb-2">
        <div className="absolute top-1 left-1 w-[calc(100%-8px)] h-12 bg-transparent border-b-[1px] border-gray-100 px-2 z-40 flex items-center gap-2">
          <button
            type="button"
            className="flex justify-center items-center px-2 w-8 h-8 rounded hover:bg-gray-100 hover:cursor-pointer disabled:opacity-50"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <ArrowUturnLeftIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex justify-center items-center px-2 w-8 h-8 rounded hover:bg-gray-100 hover:cursor-pointer disabled:opacity-50"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <ArrowUturnRightIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cn(
              "flex justify-center items-center px-2 w-8 h-8 rounded hover:bg-gray-100 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
              editor.isActive("orderedList") &&
                "bg-gray-100 text-v2-blue-text-login"
            )}
          >
            <NumberedListIcon className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={cn(
              "flex justify-center items-center px-2 w-8 h-8 rounded hover:bg-gray-100 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
              editor.isActive("bulletList") &&
                "bg-gray-100 text-v2-blue-text-login"
            )}
          >
            <ListBulletIcon className="h-6 w-6 stroke-2" />
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().liftListItem("listItem").run()
            }
            disabled={!editor.can().liftListItem("listItem")}
            className={cn(
              "flex justify-center items-center px-2 w-8 h-8 rounded hover:bg-gray-100 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            )}
          >
            <ArrowDownTrayIcon className="h-6 w-6 stroke-2 rotate-90" />
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().sinkListItem("listItem").run()
            }
            disabled={!editor.can().sinkListItem("listItem")}
            className={cn(
              "flex justify-center items-center px-2 w-8 h-8 rounded hover:bg-gray-100 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            )}
          >
            <ArrowDownTrayIcon className="h-6 w-6 stroke-2 -rotate-90" />
          </button>
        </div>
        <EditorContent
          // className={cn("ProseMirror", {
          //   error: !!error,
          // })}
          editor={editor}
        />
      </div>

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

TipTapEditor.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};
